import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

import { IS_PC } from '@/common/constant/platform';
import { showLog } from '@/renderer/scripts/utils/tools';
import xml2json from '@/renderer/scripts/libs/xml2json';
import { getProxyConfigFromStorage } from '@/renderer/scripts/proxy';
import dnsCache from '@/renderer/scripts/libs/smh-sdk-class/packages/http/DNSCache';

let adapter: any;
let httpAgent: any;
let httpsAgent: any;
let httpRequestInstance: AxiosInstance | null;
// let ProxyAgent: any;
// let agent: any;
if (IS_PC) {
  adapter = (window.parent as any).require('axios/lib/adapters/http');
  const http = (window.parent as any).require('http');
  const https = (window.parent as any).require('https');
  const proxyConfig = getProxyConfigFromStorage();
  const { httpsOverHttp, httpOverHttp } = (window.parent as any).require('tunnel-agent');
  if (proxyConfig) {
    const proxy = {
      host: proxyConfig.host,
      port: proxyConfig.port,
    };
    httpsAgent = httpsOverHttp({
      proxy,
    });
    httpAgent = httpOverHttp({
      proxy,
    });
  } else {
    httpAgent = new http.Agent({ keepAlive: true });
    httpsAgent = new https.Agent({ keepAlive: true });
  }
}

function transformXMLResponse(data: string): any {
  let res = data;
  try {
    if (typeof data === 'string') {
      if (data.includes('xml')) {
        res = xml2json(data);
      } else {
        res = JSON.parse(data);
      }
    } else {
      res = data;
    }
  } catch (e) {
    res = data;
  }
  return res;
}

/**
 * 清楚缓存会导致重新创建一个新的axios实例，
 * 在创建过程中，会加载最新的配置，从而达到更新各种配置的效果
 *
 */
export const clearRequestCache = () => {
  httpRequestInstance = null;
};
/**
 * 触发requestInstance自主更新最新的配置
 */
export const emitUpdateRequestConfig = () => {
  if (!httpRequestInstance) return;
  const proxyConfig = getProxyConfigFromStorage(true);
  const { httpsOverHttp, httpOverHttp } = (window.parent as any).require('tunnel-agent');
  if (proxyConfig) {
    const proxy = {
      host: proxyConfig.host,
      port: proxyConfig.port,
    };
    httpRequestInstance.defaults.httpsAgent = httpsOverHttp({
      proxy,
    });
    httpRequestInstance.defaults.httpAgent = httpOverHttp({
      proxy,
    });
  } else {
    const http = (window.parent as any).require('http');
    const https = (window.parent as any).require('https');
    httpRequestInstance.defaults.httpsAgent = new https.Agent({ keepAlive: true });
    httpRequestInstance.defaults.httpAgent = new http.Agent({ keepAlive: true });
  }
};
/**
 * @description 获取 COS 请求示例
 * @return AxiosInstance
 * @param params
 */
export default function getRequest(
  params: {
    maxBodyLength?: number;
    logPrefix?: string;
    logTag?: keyof loggerFunc;
  } = {},
): AxiosInstance {
  const logTag = params.logTag ?? 'smh';
  // 实现为单例
  if (httpRequestInstance) return httpRequestInstance;
  // TODO 由于找不到既可以满足单例，又能够满足自定义记录logPrefix的前缀的方式，暂时固定
  const logPrefix = 'HttpRequest';
  const config: Record<string, any> = {
    timeout: 50000,
    maxBodyLength: params.maxBodyLength || 30 * 1024 * 1024,
  };
  if (IS_PC) {
    config.adapter = adapter;
    config.httpAgent = httpAgent;
    config.httpsAgent = httpsAgent;
    config.maxRedirects = 0;
    const proxyConfig = getProxyConfigFromStorage();
    const { httpsOverHttp, httpOverHttp } = (window.parent as any).require('tunnel-agent');
    if (proxyConfig) {
      const proxy = {
        host: proxyConfig.host,
        port: proxyConfig.port,
      };
      config.httpsAgent = httpsOverHttp({
        proxy,
      });
      config.httpAgent = httpOverHttp({
        proxy,
      });
    } else {
      const http = (window.parent as any).require('http');
      const https = (window.parent as any).require('https');
      config.httpsAgent = new https.Agent({
        keepAlive: true,
      });
      config.httpAgent = new http.Agent({
        keepAlive: true,
      });
    }
  }
  const options: AxiosRequestConfig = {
    // 是否跨站点访问控制请求
    // withCredentials: true,
    ...config,
    // transformRequest: [data => {
    //   JSON.stringify(data);
    // }],
    transformResponse: [transformXMLResponse],
    validateStatus(status: number) {
      // 200 202 204 206返回给response 否则交给catch处理
      return Math.floor(status / 100) === 2; // 200 202 204 206
    },
  };

  const cosRequest = axios.create(options);
  cosRequest.interceptors.request.use(
    (config: any) => {
      config.starttime = Date.now();
      const logParams = showLog(`${logPrefix} request`, null, null, config);
      logParams && logger[logTag](...logParams);
      return config;
    },
    error => Promise.reject(error),
  );

  // 设置响应拦截器
  cosRequest.interceptors.response.use(
    (response): Promise<any> => {
      const { data, status, headers, config } = response;
      const res = {
        data,
        status,
        headers,
        // cos: x-cos-request-id; csp:x-amz-request-id
        requestId: headers['x-cos-request-id'] || headers['x-amz-request-id'],
      };
      const logParams = showLog(`${logPrefix} response`, null, res, {
        ...config,
        endtime: Date.now(),
      });
      logParams && logger.smh(...logParams);
      return Promise.resolve(res);
    },
    (error): Promise<any> =>
      new Promise((resolve, reject) => {
        let res: any;
        if (error?.response?.data) {
          const { data } = error.response;
          if (IS_PC && data.pipe) {
            // 若错误返回的是流
            const chunkList: any[] | Uint8Array | number[] = [];
            const dataHandler = (chunk: number) => {
              chunkList.push(chunk);
            };
            // eslint-disable-next-line consistent-return
            const endHandler = () => {
              let body;
              try {
                body = Buffer.concat(chunkList as readonly Uint8Array[]);
              } catch (error) {
                return reject(res);
              }
              const err = transformXMLResponse(body.toString()) as any;
              res = {
                code: err.Error?.Code || 'HttpError',
                message: err.Error?.Message || 'HttpError',
                headers: error.response?.headers || {},
                status: error.response?.status || -1,
                requestId: err.Error?.RequestId,
              };
              const logParams = showLog(`${logPrefix} response`, res, null, {
                ...error.config,
                endtime: Date.now(),
              });
              logParams && logger.smh(...logParams);
              return reject(res);
            };
            data.on('data', dataHandler);
            data.on('end', endHandler);
          } else {
            res = {
              code: data?.Error?.Code || error.code || 'HttpError',
              message: data.Error?.Message || error.message || 'HttpError',
              headers: error.response?.headers || {},
              status: error.response?.status || -1,
              requestId: data?.Error?.RequestId,
            };
            const logParams = showLog(`${logPrefix} response`, res, null, {
              ...error.config,
              endtime: Date.now(),
            });
            logParams && logger.smh(...logParams);
            reject(res);
          }
        } else {
          res = error.toJSON();
          const logParams = showLog(`${logPrefix} response`, res, null, {
            ...error.config,
            endtime: Date.now(),
          });
          logParams && logger.smh(...logParams);
          reject(res);
        }
      }),
  );
  dnsCache.registerIntercepter(cosRequest);
  httpRequestInstance = cosRequest;
  return cosRequest;
}
